import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import { Add } from '@mui/icons-material';
import { Backdrop, Box, Button, CircularProgress, Stack } from '@mui/material';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import Container from 'src/components/atoms/Container';
import { CustomNoRowsOverlay } from 'src/components/molecules/NoRowsOverlay/NoRowsOverlay';
import { defaultNonSortableColDef } from 'src/components/organisms/DataTable/columns/default-options';
import { DataTable } from 'src/components/organisms/DataTable/DataTable';
import { DeleteDialog } from 'src/components/organisms/DeleteDialog/DeleteDialog';
import { SmartModuleTitleCard } from 'src/components/organisms/SmartModuleTitleCard/SmartModuleTitleCard';
import { TableWrapper } from 'src/components/styled/TableWrapper';
import { BuyingEventRow } from 'src/domain/table/buying-event-row';
import { MaybeBoolean, MaybeNumber, MaybeNumberArray, MaybeString } from 'src/domain/types/maybe-types';
import { useChainId } from 'src/hooks';
import { useCustomAgGridNavigation } from 'src/hooks/ag-grid/useCustomAgGridNavigation';
import { useBuyingEventManagementTable } from 'src/hooks/buying-event-managment/table/useBuyingEventManagementTable';
import { useChangesSnackbar } from 'src/hooks/snackbar/useChangesSnackbar';
import { useSnackbar } from 'src/hooks/snackbar/useSnackbar';
import { useApiMutation } from 'src/infrastructure/rest-api/useApi';
import { formatMonthKeyToDate } from 'src/utils/monthKeys';
import { BuyingEventDialog } from '../../organisms/BuyingEventDialog/BuyingEventDialog';

type BuyingEvent = {
    id?: MaybeNumber;
    name?: MaybeString;
    orderDate?: MaybeNumber;
    deliveryRangeStart?: MaybeNumber;
    deliveryRangeEnd?: MaybeNumber;
    items?: MaybeNumberArray;
    isDefault?: MaybeBoolean;
};

export const BuyingEventManagement: FC = () => {
    const chainId = useChainId();
    const {
        loading: dataLoading,
        data: buyingEventRows,
        columns: buyingEventColumns,
        refetch,
        planningLevelList,
        error,
    } = useBuyingEventManagementTable();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [event, setEvent] = useState<BuyingEvent | undefined>();
    const [itemPendingDeletion, setItemPendingDeletion] = useState<BuyingEvent | null>(null);
    const showSnackbar = useSnackbar();
    const showChangesSnackbar = useChangesSnackbar();

    const [deleteEvent, { loading: deletingEvent }] = useApiMutation('/api/buyingevents/{id}', 'delete', {
        update(data, _variables, queryClient) {
            const path = { chaidId: chainId };
            queryClient.setQueryData(['/api/buyingevents/chains/{chainId}', path], data);
        },
    });

    const onAddEvent = () => {
        setEvent(undefined);
        setDialogOpen(true);
    };

    const showDeleteDialog = useCallback((event: BuyingEvent | null) => {
        setItemPendingDeletion(event);
    }, []);

    const handleCancelDeletion = useCallback(() => {
        setItemPendingDeletion(null);
    }, []);

    const handleDeleteEvent = useCallback(async () => {
        if (itemPendingDeletion?.id === undefined || itemPendingDeletion?.id === null) return;
        deleteEvent({ path: { id: itemPendingDeletion.id } })
            .then(() => {
                refetch();
                showChangesSnackbar();
            })
            .catch((err) => {
                showChangesSnackbar(err);
            })
            .finally(() => {
                setEvent(undefined);
                setItemPendingDeletion(null);
            });
    }, [deleteEvent, itemPendingDeletion, refetch, showChangesSnackbar]);

    const handleEditRow = (row: BuyingEventRow) => {
        const {
            id,
            name,
            orderMonth: orderDate,
            buyingStart: deliveryRangeStart,
            buyingEnd: deliveryRangeEnd,
            plannedOnIds: items,
            isDefault,
        } = row;
        if (!id || !name || !orderDate || !deliveryRangeStart || !deliveryRangeEnd || !items) return;
        setEvent({
            id,
            name,
            orderDate,
            deliveryRangeStart: deliveryRangeStart,
            deliveryRangeEnd: deliveryRangeEnd,
            items,
            isDefault: isDefault ?? false,
        });
        setDialogOpen(true);
    };

    const noRowsOverlayComponentParams = useMemo(() => {
        return {
            error: !!error,
            noRowsMessageFunc: () => (error ? error.message : 'No events created yet.'),
        };
    }, [error]);

    const onCloseDialog = () => {
        setDialogOpen(false);
        setEvent(undefined);
    };

    const gridApiRef = useRef<GridApi | null>(null);
    const tableWrapperRef = useRef<HTMLDivElement>(null);

    const onGridReady = useCallback(({ api }: GridReadyEvent) => {
        gridApiRef.current = api;
    }, []);

    useCustomAgGridNavigation({
        api: gridApiRef.current, // This will be set in the effect below
        tableWrapperRef,
    });

    useKeyboardShortcut({
        shortcutName: 'NEW',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            onAddEvent();
        },
    });

    useKeyboardShortcut({
        shortcutName: 'EDIT/VIEW',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            const selectedRows = gridApiRef.current?.getSelectedNodes();
            if (selectedRows?.length === 1) {
                const row = selectedRows[0].data as BuyingEventRow;
                handleEditRow(row);
            } else {
                showSnackbar('Please select a single buying event to edit.', 'warning');
            }
        },
    });

    useKeyboardShortcut({
        shortcutName: 'DELETE',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            const selectedRows = gridApiRef.current?.getSelectedNodes();
            if (selectedRows?.length === 1) {
                const row = selectedRows[0].data as BuyingEventRow;
                showDeleteDialog(row);
            } else {
                showSnackbar('Please select a single buying event to delete.', 'warning');
            }
        },
    });

    return (
        <Stack spacing={2}>
            <SmartModuleTitleCard />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}
            >
                <Button
                    loadingPosition="start"
                    size="medium"
                    color="info"
                    variant={'contained'}
                    startIcon={<Add />}
                    onClick={onAddEvent}
                >
                    Add event
                </Button>
            </Box>
            <Container>
                {dialogOpen && (
                    <BuyingEventDialog
                        id={event?.id}
                        eventName={event?.name}
                        eventOrderMonth={formatMonthKeyToDate(event?.orderDate)}
                        initialDeliveryRangeStart={formatMonthKeyToDate(event?.deliveryRangeStart)}
                        initialDeliveryRangeEnd={formatMonthKeyToDate(event?.deliveryRangeEnd)}
                        plannedOnIds={event?.items}
                        planningLevelList={planningLevelList}
                        open
                        handleCloseDialog={onCloseDialog}
                        handleRefetch={refetch}
                    />
                )}

                <TableWrapper sx={{ flex: 1 }} className="ag-theme-alpine-custom" ref={tableWrapperRef}>
                    <DataTable
                        onGridReady={onGridReady}
                        rows={buyingEventRows}
                        columns={buyingEventColumns}
                        defaultColDef={defaultNonSortableColDef}
                        context={{
                            showDeleteDialog,
                            handleEditRow,
                        }}
                        loading={dataLoading}
                        noRowsOverlayComponent={CustomNoRowsOverlay}
                        noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                        rowSelection={{
                            mode: 'singleRow',
                            checkboxes: false,
                            enableClickSelection: true,
                        }}
                        suppressCellFocus
                    />
                </TableWrapper>
                {itemPendingDeletion && (
                    <DeleteDialog
                        open={!!itemPendingDeletion}
                        onCancel={handleCancelDeletion}
                        onDelete={handleDeleteEvent}
                        title="Delete buying event?"
                        content={`Are you sure you want to delete the buying event '${itemPendingDeletion.name}'? Deleting this buying event is permanent and cannot be undone.`}
                    />
                )}
                <Backdrop
                    sx={{
                        color: '#fff',
                        zIndex: (theme) => theme.zIndex.modal + 1,
                        position: 'absolute',
                    }}
                    open={deletingEvent}
                >
                    {deletingEvent && <CircularProgress color="inherit" />}
                </Backdrop>
            </Container>
        </Stack>
    );
};
